/* eslint-disable @typescript-eslint/no-explicit-any */
import { LocalDateTime } from 'js-joda';
import _ from 'lodash';

import * as Service from 'types/service';
import { BusinessHoursWeekdayMap, dashboard } from 'types';
import {
  FALLBACK_SERVICE_TYPE,
  getShouldEnablePartyNotifyIndicatorFired,
  hasBusinessProfiles,
} from 'utils/partyUtils';
import { NO_SEATING_AREA_TEXT } from 'utils/seatingAreaUtils';
import { SelectedExperience } from 'selectors/calendarFilterSelectors';
import { adjustForBusinessDay, fastParseDateTime, getBusinessDay } from 'utils/timeUtils';
import { core, safeEnumCompare, toEnumString } from '@tock/coretypes';
import { defaultMemoize } from 'reselect';
import { getTagsForProfile } from 'utils/tagUtils';
import { oc } from 'ts-optchain';
import protos from 'utils/protos';

const { Service: ServiceEnum } = protos;

interface PartyFilters {
  partySize: number;
  serviceType?: string;
  experienceName?: string | null;
  experienceId?: number | null;
  seatingOptionEnumValue?: string | null;
  variety?: core.TicketType.Variety;
  guestProfile?: dashboard.IGuestProfile | null;
}

// Utility to extract filter values from a service party
const getPartyFilterValues = (party: Service.Party): PartyFilters => ({
  partySize: party.partySize,
  serviceType: party.serviceTypeString,
  experienceName: party.experience || party.type,
  experienceId: party.experienceId,
  variety: party.purchase?.ticketTypeVariety,
  seatingOptionEnumValue: party.purchase?.seatingAreaEnumValue,
  guestProfile: hasBusinessProfiles(party) ? party.primaryDinerProfile : undefined,
});

// Utility to extract filter values from a waitlist party
const getWaitlistFilterValues = (waitlistParty: dashboard.CalendarWaitlist): PartyFilters => ({
  partySize: waitlistParty.desiredSize,
  serviceType: waitlistParty.desiredService || FALLBACK_SERVICE_TYPE,
  experienceName: oc(waitlistParty).desiredExperience.name(),
  experienceId: oc(waitlistParty).desiredExperience.id(),
  guestProfile: waitlistParty.guestProfile,
});

const filterBySelectedPartyListExperiences = (
  selectedPartyListExperiences: SelectedExperience[],
  experienceQuery: { experienceName?: string | null; experienceId?: number | null }
): boolean => {
  const { experienceName, experienceId } = experienceQuery;

  if (_.isEmpty(selectedPartyListExperiences) || (!experienceId && !experienceName)) {
    return true;
  }

  if (experienceName === 'WalkIn') {
    return !!_.find(selectedPartyListExperiences, { name: 'Walk-in' });
  }

  // For party data pre 8/30/2019 we will only have the name.
  // If the ID is there we want to use it for comparison as names are not guaranteed to be unique
  const matcher = experienceId ? { id: experienceId } : { name: experienceName };
  return !!_.find(selectedPartyListExperiences, matcher);
};

const filterBySelectedGuestListTab = (
  selectedGuestListTab: 'guestList' | 'notifyList' | 'orderList',
  variety?: core.TicketType.Variety
): boolean => {
  switch (selectedGuestListTab) {
    case 'guestList':
      return variety !== 'ORDER_PICKUP' && variety !== 'ORDER_DELIVERY';
    case 'notifyList':
      return false;
    case 'orderList':
      return variety === 'ORDER_PICKUP' || variety === 'ORDER_DELIVERY';
    default:
      return true;
  }
};

export const filterBySelectedPartyListSeatingOptions = (
  selectedPartyListSeatingOptions: string[],
  seatingOptionEnumValue: string | null | undefined
): boolean => {
  if (_.isEmpty(selectedPartyListSeatingOptions)) {
    return true;
  }

  if (_.isEmpty(seatingOptionEnumValue)) {
    return selectedPartyListSeatingOptions.includes(NO_SEATING_AREA_TEXT);
  }

  return selectedPartyListSeatingOptions.includes(seatingOptionEnumValue as string);
};

/**
 * Determines if the supplied time fits within the bounds of any of the
 * selected services.
 *
 * Do not use this method within the context of timeline and service.
 * Instead rely party.serviceTypeString or getInferredServiceTypeString()
 */
export const matchesSelectedPartyListServices = (
  publicHours: BusinessHoursWeekdayMap,
  selectedPartyListServices: number[],
  dateTime: LocalDateTime
): boolean => {
  if (_.isEmpty(selectedPartyListServices)) {
    return true;
  }

  const businessLocalDate = getBusinessDay(dateTime);
  const weekday = businessLocalDate.dayOfWeek().toString();
  return _.some(selectedPartyListServices, (value: number) => {
    const serviceType = toEnumString(ServiceEnum.Type, value);
    const service = _.find(publicHours[weekday], (service) => service.serviceType === serviceType);
    if (!service) {
      return false;
    }

    return (
      !dateTime.isBefore(adjustForBusinessDay(businessLocalDate, service.startTime)) &&
      !dateTime.isAfter(adjustForBusinessDay(businessLocalDate, service.endTime))
    );
  });
};

const filterBySelectedPartyListServices = (
  selectedPartyListServices: number[],
  serviceTypeString?: string
): boolean => {
  if (_.isEmpty(selectedPartyListServices)) {
    return true;
  }

  if (_.isUndefined(serviceTypeString)) {
    return true;
  }

  return _.some(selectedPartyListServices, (value: number) => {
    return safeEnumCompare(serviceTypeString, ServiceEnum.Type, value);
  });
};

const filterBySelectedPartyListSizes = (
  selectedPartyListSizes: string[],
  partySize: number
): boolean => {
  if (_.isEmpty(selectedPartyListSizes)) {
    return true;
  }

  let mappedSize;
  if (partySize >= 10) {
    mappedSize = '10';
  } else {
    mappedSize = _.toString(partySize);
  }

  return _.includes(selectedPartyListSizes, mappedSize);
};

const filterBySelectedPartyListStatus = (
  selectedPartyListStatus: string[],
  party: Service.Party
): boolean => {
  if (_.isEmpty(selectedPartyListStatus)) {
    return true;
  }
  const stateString = party.state + '-' + (party.secondaryState || '');
  return _.includes(selectedPartyListStatus, stateString);
};

const filterByVisitTagsParty = (selectedVisitTags: string[], party: Service.Party) => {
  if (_.isEmpty(selectedVisitTags)) {
    return true;
  }

  const visit = party.visit;
  if (!visit) {
    return false;
  }

  return filterByVisitTags(selectedVisitTags, visit);
};

const filterByVisitTags = (visitTags: string[], visit: core.IVisit): boolean => {
  return _.some(visit.tag, (tag) => _.includes(visitTags, tag));
};

const filterByGuestTags = (
  guestTags: string[],
  guestProfile?: dashboard.IGuestProfile | null
): boolean => {
  // QQ (YK 2019-05-09): Is this right? Should we always include non-associated walk-ins?
  if (_.isEmpty(guestTags)) {
    return true;
  }

  if (!guestProfile) {
    // this is a walk-in with no associated profile, for example.
    return false;
  }

  return _.some(getTagsForProfile(guestProfile), (tag) => _.includes(guestTags, tag));
};

const filterBySupplements = (selectedSupplementIds: number[], party: Service.Party): boolean => {
  if (_.isEmpty(selectedSupplementIds)) {
    return true;
  }

  if (!party.purchase) {
    return false;
  }

  return _.some(party.purchase.purchasedOption, (supplement) =>
    _.includes(selectedSupplementIds, supplement.id)
  );
};

const filterByQuestionsUnanswered = (
  params: { [showUnansweredQuestions: string]: boolean },
  party: Service.Party
): boolean => {
  if (!params) {
    return true;
  }

  const { showUnansweredQuestions } = params;
  if (!showUnansweredQuestions) {
    return true;
  }
  return party.purchase ? !party.purchase.question || _.isEmpty(party.purchase.question) : true;
};

const filterByUnfiredItems = (
  params: { [showUnfiredItems: string]: boolean },
  party: Service.Party
): boolean => {
  if (!params) {
    return true;
  }

  const { showUnfiredItems } = params;
  if (!showUnfiredItems) {
    return true;
  }
  return getShouldEnablePartyNotifyIndicatorFired(party);
};

const filterByAmountDue = (
  params: { [showAmountDue: string]: boolean },
  party: Service.Party
): boolean => {
  if (!params) {
    return true;
  }

  const { showAmountDue } = params;
  if (!showAmountDue) {
    return true;
  }
  return (party.purchase?.amountDueCents || 0) > 0;
};

const getAllParties = defaultMemoize((partyList: Service.PartyList) => {
  let { sections } = partyList.upcoming;

  if (!_.isEmpty(partyList.walkins.section)) {
    sections = _.concat(sections, [partyList.walkins.section]);
  }

  return _.flatMap(sections, (section) => section.parties);
});

const getPartiesInDateTimeRange = (
  partyList: any,
  startDateTime: LocalDateTime,
  endDateTime: LocalDateTime
) => {
  return _.filter(getAllParties(partyList), (party: any) => {
    const expectedReservationTime = LocalDateTime.parse(party.expectedReservationTime);
    return (
      !expectedReservationTime.isBefore(startDateTime) &&
      !expectedReservationTime.isAfter(endDateTime)
    );
  });
};

const getEarliestAndLatestReservationTimes = defaultMemoize((partyList: Service.PartyList) => {
  let earliest = fastParseDateTime('9999-01-01T00:00');
  let latest = fastParseDateTime('1970-01-01T00:00');

  _.forEach(getAllParties(partyList), (party) => {
    if (party.expectedReservationTime) {
      const expectedDateTime = fastParseDateTime(party.expectedReservationTime);
      earliest = expectedDateTime.isBefore(earliest) ? expectedDateTime : earliest;
      latest = expectedDateTime.isAfter(latest) ? expectedDateTime : latest;
    }
  });

  return {
    earliest,
    latest,
  };
});

export {
  filterBySelectedGuestListTab,
  filterBySelectedPartyListExperiences,
  filterBySelectedPartyListServices,
  filterBySelectedPartyListSizes,
  filterBySelectedPartyListStatus,
  filterByGuestTags,
  filterBySupplements,
  filterByVisitTags,
  filterByVisitTagsParty,
  filterByQuestionsUnanswered,
  filterByUnfiredItems,
  filterByAmountDue,
  getAllParties,
  getEarliestAndLatestReservationTimes,
  getPartiesInDateTimeRange,
  getPartyFilterValues,
  getWaitlistFilterValues,
};
